import { PaidActionOptionType } from 'src/components/PaymentEdit';
import { ConstantsCodesObjectType, ConstantsCodeType } from 'src/types/constantsCodes';
import { TransportPackageType, TransportType } from 'src/types/transport';
import { UserServiceCode } from 'src/types/user';
import { checkPermission } from 'src/utils/checkPermission';
import { ProviderCompanyType } from 'src/types/providerCompany';
import { statusesTo } from './constants';

/** Transport UI currently supports pallet expedition/return only (not parcel). */
export const isTransportPalletServiceFlag = (value: number | string | boolean | null | undefined): boolean =>
    value === true || value === 1 || value === '1';

export const isProviderPalletServiceForTransportType = (
    providerService: UserServiceCode,
    transportType: string | undefined
): boolean => {
    if (!isTransportPalletServiceFlag(providerService.IS_PALLET)) {
        return false;
    }
    if (transportType === 'return') {
        return isTransportPalletServiceFlag(providerService.IS_RETURN);
    }
    // Default / expedition
    return isTransportPalletServiceFlag(providerService.IS_EXPEDITION);
};

export const filterTransportShipperCodes = (
    shipperCodes: ConstantsCodeType[],
    providerServiceCodes: UserServiceCode[],
    transportType: string | undefined
): ConstantsCodeType[] =>
    shipperCodes.filter((shipperCode) =>
        providerServiceCodes.some(
            (providerService) =>
                providerService.SHIPPER_CODE === shipperCode.value &&
                isProviderPalletServiceForTransportType(providerService, transportType)
        )
    );

export const filterTransportServiceCodes = (
    serviceCodes: ConstantsCodeType[],
    providerServiceCodes: UserServiceCode[],
    shipper: string | undefined,
    transportType: string | undefined
): ConstantsCodeType[] => {
    if (!shipper) {
        return [];
    }

    const palletProviderValues = new Set(
        providerServiceCodes
            .filter(
                (providerService) =>
                    providerService.SHIPPER_CODE === shipper &&
                    isProviderPalletServiceForTransportType(providerService, transportType)
            )
            .map((providerService) => providerService.VALUE)
    );

    return serviceCodes.filter((serviceCode) => {
        if (serviceCode.shipperCode !== shipper) {
            return false;
        }
        if (!palletProviderValues.has(String(serviceCode.value))) {
            return false;
        }
        // Prefer provider-scoped pallet list; also keep constants pallet flag when present.
        if (serviceCode.isPallet !== undefined && serviceCode.isPallet !== null) {
            return isTransportPalletServiceFlag(serviceCode.isPallet);
        }
        return true;
    });
};

/** Ensures the currently stored value is present in options so a Select can display it. */
export const withCurrentOption = (
    filtered: ConstantsCodeType[],
    allCodes: ConstantsCodeType[],
    currentValue: string | undefined | null
): ConstantsCodeType[] => {
    if (!currentValue || filtered.some((code) => String(code.value) === String(currentValue))) {
        return filtered;
    }
    const known = allCodes.find((code) => String(code.value) === String(currentValue));
    return [...filtered, known || { label: currentValue, value: currentValue }];
};

export const getCloneTransport = (data: TransportType): TransportType => {
    const packages: TransportPackageType[] = (data.PACKAGES || [])
        .filter((pkg) => pkg.LINE_STATE !== 'D')
        .map((pkg, index) => ({
            ID: -1 - index,
            PACKAGE_TYPE: pkg.PACKAGE_TYPE,
            WEIGHT: pkg.WEIGHT,
            WIDTH: pkg.WIDTH,
            HEIGHT: pkg.HEIGHT,
            LENGTH: pkg.LENGTH,
            PRICE: pkg.PRICE,
            CURRENCY: pkg.CURRENCY,
            VOLUME: pkg.VOLUME,
            LOADING_METERS: pkg.LOADING_METERS,
            EXPEDITION_BOX_NUMBER: pkg.EXPEDITION_BOX_NUMBER,
            LINE_STATE: 'E',
            STATUS: 'created',
        }));

    return {
        TYPE: data.TYPE || 'expedition',
        STATUS: 'created',
        ESHOP_TRANSPORT_ID: undefined,
        SHIPPER: data.SHIPPER,
        SHIPPER_SERVICE_CODE: data.SHIPPER_SERVICE_CODE,
        WEIGHT: data.WEIGHT,
        UNIT: data.UNIT,
        PRICE: data.PRICE,
        SHIPPING_PRICE: data.SHIPPING_PRICE,
        CURRENCY: data.CURRENCY,
        SHIPPING_CURRENCY: data.SHIPPING_CURRENCY,
        PAID: data.PAID ?? 0,
        PAYMENT_METHOD: data.PAYMENT_METHOD,
        COD_PRICE: data.COD_PRICE,
        COD_CURRENCY: data.COD_CURRENCY,
        COD_REF_NUMBER: data.COD_REF_NUMBER,
        MESSAGE: data.MESSAGE,
        INVOICE_NUMBER: data.INVOICE_NUMBER,
        ORDER_BOX_COUNT: data.ORDER_BOX_COUNT,
        TRANSPORT_PAID_BY_SKLADON: data.TRANSPORT_PAID_BY_SKLADON,
        AGECHECK: data.AGECHECK,
        PICK_UP_AT: data.PICK_UP_AT,
        RECIPIENT_NAME: data.RECIPIENT_NAME,
        RECIPIENT_ATTENTION_NAME: data.RECIPIENT_ATTENTION_NAME,
        RECIPIENT_EMAIL: data.RECIPIENT_EMAIL,
        RECIPIENT_PHONE: data.RECIPIENT_PHONE,
        RECIPIENT_COMPANY_NAME: data.RECIPIENT_COMPANY_NAME,
        RECIPIENT_COMPANY_ID: data.RECIPIENT_COMPANY_ID,
        RECIPIENT_TAX_ID: data.RECIPIENT_TAX_ID,
        RECIPIENT_STREET: data.RECIPIENT_STREET,
        RECIPIENT_ADDRESS_ADDITIONAL_INFO: data.RECIPIENT_ADDRESS_ADDITIONAL_INFO,
        RECIPIENT_ADDRESS_ADDITIONAL_INFO_2: data.RECIPIENT_ADDRESS_ADDITIONAL_INFO_2,
        RECIPIENT_CITY: data.RECIPIENT_CITY,
        RECIPIENT_STATE: data.RECIPIENT_STATE,
        RECIPIENT_PROVINCE_CODE: data.RECIPIENT_PROVINCE_CODE,
        RECIPIENT_POST_CODE: data.RECIPIENT_POST_CODE,
        SENDER_NAME: data.SENDER_NAME,
        SENDER_ATTENTION_NAME: data.SENDER_ATTENTION_NAME,
        SENDER_EMAIL: data.SENDER_EMAIL,
        SENDER_PHONE: data.SENDER_PHONE,
        SENDER_COMPANY_NAME: data.SENDER_COMPANY_NAME,
        SENDER_COMPANY_ID: data.SENDER_COMPANY_ID,
        SENDER_TAX_ID: data.SENDER_TAX_ID,
        SENDER_STREET: data.SENDER_STREET,
        SENDER_ADDRESS_ADDITIONAL_INFO: data.SENDER_ADDRESS_ADDITIONAL_INFO,
        SENDER_ADDRESS_ADDITIONAL_INFO_2: data.SENDER_ADDRESS_ADDITIONAL_INFO_2,
        SENDER_CITY: data.SENDER_CITY,
        SENDER_STATE: data.SENDER_STATE,
        SENDER_PROVINCE_CODE: data.SENDER_PROVINCE_CODE,
        SENDER_POST_CODE: data.SENDER_POST_CODE,
        PACKAGES: packages,
    };
};

export const getAllowedStatuses = (
    transportStatusCodes: ConstantsCodeType[],
    transportStatusCodesObject: ConstantsCodesObjectType,
    transportData?: { STATUS?: string } | null
): ConstantsCodeType[] => {
    if (checkPermission('TRANSPORT.EDITALL')) {
        return transportStatusCodes;
    }

    if (!transportData || !transportData.STATUS) {
        return [];
    }

    return [
        transportStatusCodesObject[transportData && transportData.STATUS],
        ...(statusesTo[transportData && transportData.STATUS] || [])
            .filter((statusKey) => {
                return checkPermission(`TRANSPORT.STATUS.LIST.${statusKey.toUpperCase()}`);
            })
            .map((statusKey) => {
                return transportStatusCodesObject[statusKey];
            }),
    ];
};

export const getPaidData = (
    paidOption: PaidActionOptionType,
    dataSrc: TransportType,
    confirmData?: { data: TransportType }
) => {
    const dataSave = { ...dataSrc };
    dataSave.STATUS = paidOption.STATUS;
    dataSave.PAYMENT_METHOD = paidOption.PAYMENT_METHOD;
    dataSave.PAID = paidOption.PAID;

    if (confirmData && paidOption.label === 'cashOnDelivery') {
        dataSave.COD_PRICE = confirmData.data.COD_PRICE;
        dataSave.COD_CURRENCY = confirmData.data.COD_CURRENCY;
        dataSave.COD_REF_NUMBER = confirmData.data.COD_REF_NUMBER;
    }

    return dataSave;
};

export const getChangesProviderCompaniesTransport = (
    data: ProviderCompanyType,
    type: 'recipient' | 'sender'
): TransportType => {
    const prefix = type === 'sender' ? 'SENDER' : 'RECIPIENT';
    const values: TransportType = {
        [`${prefix}_NAME`]: data.RECIPIENT_NAME,
        [`${prefix}_ATTENTION_NAME`]: data.CONTACT_PERSON,
        [`${prefix}_COMPANY_NAME`]: data.COMPANY_NAME,
        [`${prefix}_EMAIL`]: data.EMAIL,
        [`${prefix}_PHONE`]: data.PHONE,
        [`${prefix}_STREET`]: data.STREET,
        [`${prefix}_CITY`]: data.CITY,
        [`${prefix}_POST_CODE`]: data.ZIP_CODE,
        [`${prefix}_STATE`]: data.STATE,
    };
    if (type === 'recipient') {
        values.RECIPIENT_COMPANY_ID = data.COMPANY_ID;
        values.RECIPIENT_ADDRESS_ADDITIONAL_INFO = data.ADDRESS_ADDITIONAL_INFO;
        values.RECIPIENT_PROVINCE_CODE = data.PROVINCE_CODE;
    }
    return values;
};
